import React, { ReactNode, useEffect, useState } from 'react';
import { PublicationContext } from 'centrifuge';

import centrifuge from '@/ws/config.ts';
import { Contact, DialogMessage } from '@/api/messaging/types.ts';
import { WebSocketEvents } from '@/enums/apiEnums.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { handleEvents } from '@/providers/WebSocketProvider/utils';
import {
  OneTimePurchaseSocketResponse,
  SubscriptionPurchaseSocketResponse
} from '@/api/purchases/types.ts';
import { ITonTransaction, TonChallenge } from '@/api/ton/types.ts';

export interface WebSocketMessage {
  type: WebSocketEvents;
  message_incoming_event?: DialogMessage;
  dialog_created_event?: unknown;
  user_activated_event?: unknown;
  user_banned_event?: unknown;
  user_verification_failed_event?: unknown;
  one_time_purchase_event?: OneTimePurchaseSocketResponse;
  like_created_event?: { contact: Contact };
  photo_approved_event?: { photo_hid: string };
  photo_rejected_event?: { photo_hid: string };
  dialog_request_accepted_event?: { contact: Contact };
  dialog_request_declined_event?: { contact: Contact };
  dialog_deleted_event?: { contact_hid: string };
  dialog_deactivated_event?: { contact_hid: string };
  subscription_purchase_event?: SubscriptionPurchaseSocketResponse;
  contact_deleted_event?: { contact_hid: string };
  ton_challenge_unlocked_event?: { challenge: TonChallenge };
  ton_challenge_advanced_event?: { challenge: TonChallenge };
  ton_challenge_completed_event?: { challenge: TonChallenge };
  ton_transaction_created_event?: { transaction: ITonTransaction };
  block_created_event?: { contact_hid: string };
  video_approved_event?: { video_hid: string };
  video_rejected_event?: { video_hid: string };
}

export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);

  const { aboutMe } = useProfileStore();

  const channel = aboutMe?.real_time_channels?.main;

  const processMessage = (ctxData: WebSocketMessage) => {
    const { type } = ctxData;

    const event = handleEvents[type];

    if (typeof event === 'function') {
      event(ctxData);
    }
  };

  useEffect(() => {
    if (channel && !isSubscribed) {
      const sub = centrifuge.newSubscription(channel);

      sub.on('publication', (ctx: PublicationContext) => {
        processMessage(ctx.data as WebSocketMessage);
      });

      sub.subscribe();
      setIsSubscribed(true);

      return () => {
        centrifuge.removeSubscription(sub);
        setIsSubscribed(false);
      };
    }
  }, [channel]);

  return <>{children}</>;
};
